// SavedViewItem — one row in the SavedView dropdown.
//
// Anatomy: [name (load)]  [AOI N badge if any polygons]  [Alert me ☐/☑]  [×]
//
// The "Alert me" checkbox is visible only when `polygonCount > 0` — toggling
// it on a view with zero AOIs is meaningless and the API rejects it 400.
//
// Visual conventions follow the Bloomberg-density theme used across
// `packages/ui` and `packages/ui-filters`: 22px row, 11px mono, no shadows.

import type { CSSProperties } from 'react';

export interface SavedViewItemProps {
  id: string;
  name: string;
  description?: string | null;
  polygonCount: number;
  alertActive: boolean;
  /** True while a load/delete/toggle round-trip is in flight; row is dimmed. */
  pending?: boolean;
  onLoad: () => void;
  onDelete: () => void;
  onAlertToggle: (next: boolean) => void;
}

export function SavedViewItem({
  name,
  description,
  polygonCount,
  alertActive,
  pending,
  onLoad,
  onDelete,
  onAlertToggle,
}: SavedViewItemProps) {
  return (
    <div
      data-component="SavedViewItem"
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 22,
        gap: 6,
        padding: '0 6px',
        opacity: pending ? 0.5 : 1,
      }}
    >
      <button
        type="button"
        onClick={onLoad}
        title={description ?? `load ${name}`}
        style={{
          ...resetButton,
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          color: 'var(--fg-1)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </button>

      {polygonCount > 0 && (
        <span
          aria-label={`${polygonCount} AOI ${polygonCount === 1 ? 'polygon' : 'polygons'}`}
          title={`${polygonCount} AOI ${polygonCount === 1 ? 'polygon' : 'polygons'}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 16,
            padding: '0 4px',
            background: 'rgba(0, 212, 255, 0.10)',
            border: '1px solid rgba(0, 212, 255, 0.45)',
            color: 'var(--fg-2)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 0.4,
          }}
        >
          AOI {polygonCount}
        </span>
      )}

      {polygonCount > 0 && (
        <label
          title="alert me when a new signal hits any AOI in this view"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            cursor: pending ? 'not-allowed' : 'pointer',
            color: 'var(--fg-3)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: 0.4,
          }}
        >
          <input
            type="checkbox"
            checked={alertActive}
            disabled={pending}
            onChange={(e) => onAlertToggle(e.target.checked)}
            aria-label={`toggle AOI alert for ${name}`}
            style={{ accentColor: 'var(--primary)' }}
          />
          ALERT
        </label>
      )}

      <button
        type="button"
        onClick={onDelete}
        aria-label={`delete saved view ${name}`}
        title="delete"
        style={{
          ...resetButton,
          color: 'var(--fg-3)',
          fontSize: 12,
          padding: '0 2px',
        }}
      >
        ×
      </button>
    </div>
  );
}

const resetButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  padding: 0,
};
